const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const isValidCalendarDate = (value) => {
  if (!ISO_DATE_PATTERN.test(value || '')) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export const getFixedTimelineItems = (items = []) => (
  Array.isArray(items)
    ? items.filter((item) => item?.timingType === 'fixed' && isValidCalendarDate(item.date))
    : []
);

const escapeIcs = (value) => String(value || '')
  .replace(/\\/g, '\\\\')
  .replace(/\r\n|\n|\r/g, '\\n')
  .replace(/,/g, '\\,')
  .replace(/;/g, '\\;');

const utf8Octets = (character) => {
  const codePoint = character.codePointAt(0);
  if (codePoint <= 0x7f) return 1;
  if (codePoint <= 0x7ff) return 2;
  if (codePoint <= 0xffff) return 3;
  return 4;
};

const foldIcsLine = (line) => {
  const segments = [];
  let segment = '';
  let segmentOctets = 0;
  let limit = 75;

  for (const character of line) {
    const characterOctets = utf8Octets(character);
    if (segment && segmentOctets + characterOctets > limit) {
      segments.push(segment);
      segment = '';
      segmentOctets = 0;
      // A continuation line begins with one space, leaving 74 octets for data.
      limit = 74;
    }
    segment += character;
    segmentOctets += characterOctets;
  }

  segments.push(segment);
  return segments.join('\r\n ');
};

const toCalendarDate = (isoDate) => isoDate.replace(/-/g, '');

const nextCalendarDate = (isoDate) => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10).replace(/-/g, '');
};

const toCalendarTimestamp = (date) => (
  date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z')
);

export const buildTimelineCalendar = ({
  items = [],
  documentName = 'contract',
  responsibleLabel = 'Responsible',
  sourceClauseLabel = 'Source clause',
  categoryLabels = {},
  generatedAt = new Date(),
} = {}) => {
  const fixedItems = getFixedTimelineItems(items);
  const timestamp = toCalendarTimestamp(generatedAt);
  const encodedDocumentName = encodeURIComponent(documentName);

  const events = fixedItems.flatMap((item, position) => [
    'BEGIN:VEVENT',
    `UID:regalbot-${encodedDocumentName}-${item.index ?? position}-${item.date}@regalbot`,
    `DTSTAMP:${timestamp}`,
    `DTSTART;VALUE=DATE:${toCalendarDate(item.date)}`,
    `DTEND;VALUE=DATE:${nextCalendarDate(item.date)}`,
    `SUMMARY:${escapeIcs(item.title)}`,
    `DESCRIPTION:${escapeIcs(`${item.action || ''}\n${responsibleLabel}: ${item.party || '-'}\n${sourceClauseLabel} ${(item.clauseIndex ?? 0) + 1}: ${item.evidence || ''}`)}`,
    `CATEGORIES:${escapeIcs(categoryLabels[item.category] || item.category)}`,
    'END:VEVENT',
  ]);

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//RegalBot//Contract Commitments//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...events,
    'END:VCALENDAR',
    '',
  ].map(foldIcsLine).join('\r\n');
};